// Components
import * as React from 'react';
import {TagsInput} from 'BaseLayout/components/form/TagsInput';
import MultiAutocomplete, { DeleteStrategy } from '../../BaseLayout/components/form/MultiAutocomplete';

// Misc
import { PubListConfigInternal } from '../types';
import { linkingList, personRoleList, pubtypeList, cslLocaleList, citationStyleList, resourceTypeList } from '../data';
import { datePeriodsToStrings, dateStringsToPeriods, yearPeriodsToStrings, yearStringsToPeriods } from '../utils';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { ProjectSuggest } from 'Edit/components/auto-complete/ProjectSuggest';
import { OrganisationSuggest } from 'Edit/components/auto-complete/OrganisationSuggest';
import { PersonSuggest } from 'Edit/components/auto-complete/PersonSuggest';

import getLogger from '../../log';
import { Vocabulary } from '../../Vocabulary/components/Vocabulary';
import { getLanguageFromLocale } from '../../Intl/utils';

/** @jsx jsx */
import { jsx, Grid, Button, Flex, Input, Text, Select, Box, Styled} from 'theme-ui';
import { FormField, CategoryToggle, Preview, PreviewRUB, CategoryToggleMobile } from './theme';
import { Divider } from '@material-ui/core';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { OAuth2Gate, OAuth2Sender } from '../../Auth/components';
import { ReferencedPersonDTO, ReferencedWorkDTO } from '../../api/core';
import {
  MultiInputControls,
} from '../../BaseLayout/components/form/MultiInput/MultiInputControls';
import { AddButton, DeleteButton } from '../../BaseLayout/components/form/MultiInput/Buttons';
import { TextInput } from 'BaseLayout/components/form/generic/TextInput';
import { getVocabularyAsArray } from 'Vocabulary/util';

import { MultipleDateRange } from 'BaseLayout/components/form/DatePickerDateRange/MultipleDateRange';
import { ThemeContext } from 'BaseLayout/components';
import {
  PubListConfig,
  PubListConfigListTypeEnum,
  PublistConfigProjectOrganisation,
  PublistConfigProjectOrganisationDeterminationTypeEnum,
  PublistConfigProjectOrganisationFilterMembersEnum,
  PubListConfigReplacements
} from 'api/publist/models';
import { Radio } from 'BaseLayout/components/form';
import TriCheckBox from 'BaseLayout/components/form/TriCheckBox';
import { WorkSuggest } from 'Edit/components/auto-complete/WorkSuggest';
import { useBreakpointIndex } from '@theme-ui/match-media';

const logger = getLogger('PubListGenerator/component');

export interface Props {
  assetsAvailable: boolean;
  preview?: string;
  config: PubListConfigInternal;
  viewMessage?: string;
  loading?: boolean;
  emptyList?: boolean;
  onSuccess?: () => void;
  loadPreview: (config: PubListConfig) => any;
  storeConfig: (config: PubListConfig) => Promise<number> | null;
  storeNewConfig: (config: PubListConfig) => Promise<number>;
  updateConfig: (config: PubListConfigInternal) => any;
  alertShow: (message: string) => void;
  clearAllInput: (listType: PubListConfigListTypeEnum) => void;
  onClickCat?: () => void;
  catOpen?: boolean;
  catName?: string;
  goBack?: boolean;
  goBackClick?: (e) => void;
  configChanged?: (e) => void;
  mobileView?: boolean;
}

/**
 * Backend is using a slightly different PublistConfig, e.g. person Ids are stored as a number
 * while frontend is using a ReferencedPersonDTO to display furhter information such as names.
 * Therefore some fields of a PublistConfig must be mapped before sending to the backend.
 *
 * @param config The internal (frontend) PublistConfig
 *
 * @returns The external (backend) PublistConfig
 */
const formatConfig = (config: PubListConfigInternal): PubListConfig => {
  const formatedConfig: PubListConfig = {};

  formatedConfig.personIds = config.personIds.map((person: ReferencedPersonDTO): number => person.id);

  const projectOrgaConfig: PublistConfigProjectOrganisation = {};

  projectOrgaConfig.determinationType = config.projectOrganisationConfig.determinationType;
  if (projectOrgaConfig.determinationType === PublistConfigProjectOrganisationDeterminationTypeEnum.SELF) {
    projectOrgaConfig.restrictToRoles = config.projectOrganisationConfig?.restrictToRoles?.map((role) => role.abbr);
    projectOrgaConfig.excludeByRoles = config.projectOrganisationConfig?.excludeByRoles?.map((role) => role.abbr);
  }
  projectOrgaConfig.withOrganisationHierarchy = config.projectOrganisationConfig.withOrganisationHierarchy;
  projectOrgaConfig.considerEmploymentPeriod = config.projectOrganisationConfig.considerEmploymentPeriod;
  if (projectOrgaConfig.determinationType === PublistConfigProjectOrganisationDeterminationTypeEnum.MEMBERS) {
    projectOrgaConfig.filterMembers = config.projectOrganisationConfig.filterMembers;
  } else {
    projectOrgaConfig.filterMembers = PublistConfigProjectOrganisationFilterMembersEnum.ALL;
  }
  projectOrgaConfig.organisationIds = config.projectOrganisationConfig.organisationIds.map((orga) => orga.id);
  projectOrgaConfig.projectIds = config.projectOrganisationConfig.projectIds.map((project) => project.id);

  formatedConfig.projectOrganisationConfig = projectOrgaConfig;

  formatedConfig.filterIds = config.filterIds.map(filterId => filterId.id);
  formatedConfig.filterPersons = config.filterPersons.map((person: ReferencedPersonDTO): number => person.id);
  formatedConfig.keyIds = config.keyIds.map(keyId => keyId.id);
  formatedConfig.useMainName = config.useMainName;
  formatedConfig.filterByCreationDate = config.filterByCreationDate;
  formatedConfig.filterByYear = config.filterByYear;
  formatedConfig.filterByType = config.filterByType.map((type) => type.abbr);
  formatedConfig.excludeByType = config.excludeByType.map((type) => type.abbr);
  formatedConfig.filterByPr = config.filterByPr;
  formatedConfig.filterByLang = config.filterByLang.map((lang) => lang.abbr);
  formatedConfig.filterByRole = config.filterByRole.map((lang) => lang.abbr);
  formatedConfig.filterByRoleExcl = config.filterByRoleExcl.map((lang) => lang.abbr);
  formatedConfig.filterPersonByRole = config.filterPersonByRole.map((role) => role.abbr);
  formatedConfig.groupByYear = config.groupByYear;
  formatedConfig.groupByType = config.groupByType;
  formatedConfig.pubsort = config.pubsort;
  formatedConfig.toc = config.toc;
  formatedConfig.locale = config.locale.abbr;
  formatedConfig.linking = config.linking.map((l) => l.abbr);
  formatedConfig.replacements = config.replacements;
  formatedConfig.style = config.style;
  formatedConfig.name = config.name;
  formatedConfig.listType = config.listType;

  return formatedConfig;
};

const getRoles = (onChange: (e: any) => void, tags: any[], inputProps: any, lang = 'de') => {
  const roles = getVocabularyAsArray(personRoleList, lang, '*');
  return (
    <MultiAutocomplete
      onChange={onChange}
      options={roles}
      tags={tags}
      allwaysSuggest={true}
      loseFocusOnSuggestionClick={true}
      inputProps={ inputProps }
    />
  );
};

const getPubtypes = (onChange: (e: any) => void, tags: any[], inputProps: any, lang = 'de') => {
  const pubtypes = getVocabularyAsArray(pubtypeList, lang, 'all');
  return (
    <MultiAutocomplete
      onChange={onChange}
      options={pubtypes}
      tags={tags}
      allwaysSuggest={true}
      loseFocusOnSuggestionClick={true}
      inputProps={ inputProps }
    />
  );
};

const getResourceTypes = (onChange: (e: any) => void, tags: any[], inputProps: any, lang = 'de') => {
  const resourceTypes = getVocabularyAsArray(resourceTypeList, lang, 'all');
  return (
    <MultiAutocomplete
      onChange={onChange}
      options={resourceTypes}
      tags={tags}
      allwaysSuggest={true}
      loseFocusOnSuggestionClick={true}
      inputProps={ inputProps }
    />
  );
};

export interface CategoryToggleConfig {
  determination_type: boolean;
  citation: boolean;
  group_content: boolean;
  person_filter_func: boolean;
  general_filter: boolean;
  publication_highlight: boolean;
  replace_text_links: boolean;
}

const categoryToggleInitConfig: CategoryToggleConfig = {
  determination_type: false,
  citation: false,
  group_content: false,
  person_filter_func: false,
  general_filter: false,
  publication_highlight: false,
  replace_text_links: false
};

export const PublicationData: React.FC<Props> = ({
  config,
  alertShow,
  preview,
  updateConfig,
  loadPreview,
  storeConfig,
  storeNewConfig,
  assetsAvailable,
  loading,
  emptyList,
  onSuccess,
  viewMessage,
  clearAllInput,
  onClickCat,
  catOpen,
  catName,
  goBack,
  goBackClick,
  configChanged,
  mobileView
}) => {
  const replacements: PubListConfigReplacements[] = JSON.parse(JSON.stringify(config.replacements));
  const {
    filterByPr,
    groupByYear,
    groupByType,
    toc,
    useMainName
  } = config;

  window.onbeforeunload = (e) => {
    if(changed) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  useEffect(() => {
    return () => {
      window.onbeforeunload = null;
    };
  });

  const breakpoint = useBreakpointIndex({ defaultIndex: 0 });

  const updateConfigChanged = (config: PubListConfigInternal): any => {
    setChanged(true);
    return updateConfig(config);
  };

  const themeName = React.useContext(ThemeContext);

  const intl: IntlShape = useIntl();
  const [openCats, setOpenCats] = useState<CategoryToggleConfig>(categoryToggleInitConfig);
  const setCat = (cat: keyof CategoryToggleConfig) => {
    openCats[cat] = !openCats[cat];
    setOpenCats({...openCats, [cat]: openCats[cat]});
  };

  const [determinationType, setDeterminationType] = useState<PublistConfigProjectOrganisationDeterminationTypeEnum>(config.projectOrganisationConfig.determinationType);
  const [memberFilterType, setMemberFilterType] = useState<PublistConfigProjectOrganisationFilterMembersEnum>(config.projectOrganisationConfig.filterMembers);
  const [changed, setChanged] = useState<boolean>(false);
  const [showFilterOptions, setShowFilterOptions] = useState<boolean>(true);
  const [saveAnonymous, setSaveAnonymous] = useState<boolean>(false);

  const handleOnDeterminationTypeChange = (type: PublistConfigProjectOrganisationDeterminationTypeEnum) =>  {

      setDeterminationType(type);
      const temp = config.projectOrganisationConfig;
      temp.determinationType = type;

      updateConfigChanged({...config, projectOrganisationConfig: temp});
  };

  const disabledButtons: boolean = config.personIds.length + config.projectOrganisationConfig.organisationIds.length + config.projectOrganisationConfig.projectIds.length < 1
    || replacements.some((replacement: PubListConfigReplacements): boolean => !replacement.key);

  preview = preview?.replaceAll('>HB</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.hb' }) + '</a>')
    .replaceAll('>WoS</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.wos' }) + '</a>')
    .replaceAll('>PubMed</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.pubmed' }) + '</a>')
    .replaceAll('>Scopus</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.scopus' }) + '</a>')
    .replaceAll('>FullText</a>', '>' + intl.formatMessage({ id: 'publist.preview.links.fulltext' }) + '</a>');

  return (
    <>
      {(showFilterOptions || !assetsAvailable) && goBack && mobileView && (
        <div sx={{
          borderTopStyle: 'solid',
          borderTopColor: 'light',
          borderWidth: catName.includes('publist') ? '1.5px' : 0,
        }}>
          <CategoryToggleMobile
            onClick={onClickCat}
            open={catOpen}
          >
            <FormattedMessage id={catName} />
          </CategoryToggleMobile>
        </div>
      )}
      {((catOpen && (showFilterOptions || !assetsAvailable)) || !mobileView) && (
        <div className='filter-options'>
          {/* Publication list name */}
          <TextInput
            name={intl.formatMessage({id: 'publist.sidebar.listName_label'})}
            help={intl.formatMessage({id: 'publist.sidebar.listName_help'})}
            value={config.name ? config.name : ''}
            placeholder={intl.formatMessage({ id: 'publist.sidebar.listName_placeholder' })}
            onChange={(name: string) => {
              updateConfigChanged({ ...config, name });
              configChanged(true);
            }}
          />

          {/* Publication list person selection */}
          <PersonSuggest
            name={intl.formatMessage({ id: 'publist.sidebar.person_label' })}
            help={intl.formatMessage({ id: 'publist.sidebar.person_help' })}
            required={config.personIds.length + config.projectOrganisationConfig.organisationIds.length + config.projectOrganisationConfig.projectIds.length < 1}
            tags={config.personIds}
            onChange={(refPersons: ReferencedPersonDTO[]): void => {
              updateConfigChanged({
                ...config,
                personIds: refPersons
              });
              configChanged(true);
            }}
            autocompleteProps={{ deleteStrategy: DeleteStrategy.SPLICE }}
            inputProps={{ placeholder: intl.formatMessage({ id: 'publist.sidebar.person_placeholder' }) }}
          />

          {/* Publication list checkbox "use main name" */}
          <div sx={{mt:"-18px"}}>
            <FormField
              help={intl.formatMessage({ id: 'publist.sidebar.useMainName_help' })}
              label={intl.formatMessage({ id: 'publist.sidebar.useMainName_label' })}
              childrenRenderPosition="start"
            >
              <input
                type="checkbox"
                id="option-use-main-name"
                name="use-main-name"
                checked={useMainName}
                onChange={(e) => {
                  updateConfigChanged({
                    ...config,
                    useMainName: !useMainName,
                  });
                  configChanged(true);
                }}
              />
            </FormField>
          </div>

          {/* Category: Organizations and Projects */}
          <div sx={{paddingTop: "15px"}}>
            <Divider/>
            <CategoryToggle open={openCats.determination_type} onClick={() => setCat('determination_type')}>
              <FormattedMessage id="publist.sidebar.projectOrga_category" />
            </CategoryToggle>
            {openCats.determination_type && (
              <React.Fragment>
                {/* Publication list project selection */}
                <ProjectSuggest
                  name={intl.formatMessage({ id: 'publist.sidebar.project_label' })}
                  help={intl.formatMessage({ id: 'publist.sidebar.project_help' })}
                  required={config.personIds.length + config.projectOrganisationConfig.organisationIds.length + config.projectOrganisationConfig.projectIds.length < 1}
                  tags={config.projectOrganisationConfig.projectIds}
                  onChange={(ids) => {
                    const temp = config.projectOrganisationConfig;
                    temp.projectIds = ids;
                    updateConfigChanged({
                      ...config,
                      projectOrganisationConfig: temp,
                    });
                    configChanged(true);
                  }}
                  autocompleteProps={{ deleteStrategy: DeleteStrategy.SPLICE }}
                  inputProps={{ placeholder: intl.formatMessage({ id: 'publist.sidebar.project_placeholder' }) }}
                />

                {/* Publication list organization selection */}
                <OrganisationSuggest
                  name={intl.formatMessage({ id: 'publist.sidebar.organisation_label' })}
                  help={intl.formatMessage({ id: 'publist.sidebar.organisation_help' })}
                  required={config.personIds.length + config.projectOrganisationConfig.organisationIds.length + config.projectOrganisationConfig.projectIds.length < 1}
                  tags={config.projectOrganisationConfig.organisationIds}
                  onChange={(ids) => {
                    const temp = config.projectOrganisationConfig;
                    temp.organisationIds = ids;
                    updateConfigChanged({
                      ...config,
                      projectOrganisationConfig: temp,
                    });
                    configChanged(true);
                  }}
                  autocompleteProps={{ deleteStrategy: DeleteStrategy.SPLICE }}
                  inputProps={{ placeholder: intl.formatMessage({ id: 'publist.sidebar.organisation_placeholder' }) }}
                />

                {/* Publication list checkbox whether to consider selected organization's hierarchy */}
                <div sx={{mt:"-15px"}}>
                  <FormField
                    help={intl.formatMessage({ id: 'publist.sidebar.withHierarchy_help' })}
                    label={intl.formatMessage({ id: 'publist.sidebar.withHierarchy_label' })}
                    childrenRenderPosition="start"
                  >
                    <input
                      type="checkbox"
                      id="option-with-hierarchy"
                      name="with-hierarchy"
                      checked={config.projectOrganisationConfig.withOrganisationHierarchy}
                      onChange={(e) => {
                        const temp = config.projectOrganisationConfig;
                        temp.withOrganisationHierarchy = !config.projectOrganisationConfig.withOrganisationHierarchy;
                        updateConfigChanged({
                          ...config,
                          projectOrganisationConfig: temp,
                        });
                        configChanged(true);
                      }}
                      />
                  </FormField>
                </div>

                <div className='orga-project-cat'>
                  {/* Publication list radio button whether to determine publications by members of the selected projects / organizations */}
                  <FormField
                    help={intl.formatMessage({ id: 'publist.sidebar.determinationTypePersons_help' })}
                    label={intl.formatMessage({ id: 'publist.sidebar.determinationTypePersons_label' })}
                    childrenRenderPosition="start"
                    labelVariant="labelXBaseline"
                    >
                      <Radio
                        name="determination-type"
                        checked={PublistConfigProjectOrganisationDeterminationTypeEnum.MEMBERS === determinationType}
                        value={PublistConfigProjectOrganisationDeterminationTypeEnum.MEMBERS}
                        onChange={(e) => {
                          handleOnDeterminationTypeChange(PublistConfigProjectOrganisationDeterminationTypeEnum.MEMBERS);
                        }}
                        />
                  </FormField>

                  {/* Radio button for members selection: Active members, former members, all members AND checkbox whether employment periods should be considered */}
                  {determinationType === PublistConfigProjectOrganisationDeterminationTypeEnum.MEMBERS &&
                    <Box paddingLeft={'2em'}>

                      <FormField
                        help={intl.formatMessage({ id: 'publist.sidebar.considerEmploymentPeriod_help' })}
                        label={intl.formatMessage({ id: 'publist.sidebar.considerEmploymentPeriod_label' })}
                        childrenRenderPosition="start"
                      >
                        <input
                          type="checkbox"
                          id="option-consider-employment-period"
                          name="consider-employment-period"
                          checked={config.projectOrganisationConfig.considerEmploymentPeriod}
                          onChange={(e) => {
                            const temp = config.projectOrganisationConfig;
                            temp.considerEmploymentPeriod = !config.projectOrganisationConfig.considerEmploymentPeriod;
                            updateConfigChanged({
                              ...config,
                              projectOrganisationConfig: temp,
                            });
                            configChanged(true);
                          }}
                          />
                      </FormField>

                      <FormField
                        label={intl.formatMessage({ id: 'publist.sidebar.orga_project_members_filter_label' })}
                        help={intl.formatMessage({ id: 'publist.sidebar.orga_project_members_filter_help' })}
                      >
                        <Flex sx={{flexDirection: 'column'}}>
                          {Object.keys(PublistConfigProjectOrganisationFilterMembersEnum).map((memberFilter) =>
                            <Radio
                              key={memberFilter}
                              name="orga-project-filter-radio"
                              value={memberFilter}
                              checked={memberFilter as PublistConfigProjectOrganisationFilterMembersEnum === memberFilterType}
                              onChange={(e) => {
                                setMemberFilterType(memberFilter as PublistConfigProjectOrganisationFilterMembersEnum);
                                const temp = config.projectOrganisationConfig;
                                temp.filterMembers = memberFilter as PublistConfigProjectOrganisationFilterMembersEnum;
                                updateConfigChanged(
                                  {
                                  ...config,
                                  projectOrganisationConfig: temp,
                                  }
                                );
                                configChanged(true);
                              }
                            }
                            >
                              {intl.formatMessage({ id: 'publist.sidebar.orga_project_members_filter_'+memberFilter })}
                            </Radio>
                            )
                          }
                        </Flex>
                      </FormField>
                    </Box>
                  }

                  {/* Publication list radio button whether to determine publications which directly belong to the selected projects / organizations */}
                  <FormField
                    help={intl.formatMessage({ id: 'publist.sidebar.determinationTypeDirect_help' })}
                    label={intl.formatMessage({ id: 'publist.sidebar.determinationTypeDirect_label' })}
                    childrenRenderPosition="start"
                    >
                      <Radio
                        name="determination-type"
                        checked={PublistConfigProjectOrganisationDeterminationTypeEnum.DIRECT === determinationType}
                        value={PublistConfigProjectOrganisationDeterminationTypeEnum.DIRECT}
                        onChange={(e) => {
                          handleOnDeterminationTypeChange(PublistConfigProjectOrganisationDeterminationTypeEnum.DIRECT);
                        }}
                        />
                  </FormField>

                  {/* Publication list radio button whether to determine publications which the selected projects / organizations did contribute to */}
                  <FormField
                    help={intl.formatMessage({ id: 'publist.sidebar.determinationTypeSelf_help' })}
                    label={intl.formatMessage({ id: 'publist.sidebar.determinationTypeSelf_label' })}
                    childrenRenderPosition="start"
                    >
                      <Radio
                        name="determination-type"
                        checked={PublistConfigProjectOrganisationDeterminationTypeEnum.SELF === determinationType}
                        value={PublistConfigProjectOrganisationDeterminationTypeEnum.SELF}
                        onChange={() => {
                          handleOnDeterminationTypeChange(PublistConfigProjectOrganisationDeterminationTypeEnum.SELF);
                        }}
                        />
                  </FormField>

                  {determinationType === PublistConfigProjectOrganisationDeterminationTypeEnum.SELF &&
                    <Box pl={'2em'}>
                      {/* Filter works for project's / organization's contributions with certain roles. Inclusion filter */}
                      <FormField
                        label={intl.formatMessage({ id: 'publist.sidebar.orga_project_role_restriction_label' })}
                        help={intl.formatMessage({ id: 'publist.sidebar.orga_project_role_restriction_help' })}
                      >
                        {getRoles((value) => {
                          const temp = config.projectOrganisationConfig;
                          temp.restrictToRoles = value;
                          updateConfigChanged({
                            ...config,
                            projectOrganisationConfig: temp,
                          });
                          configChanged(true);
                        }, config.projectOrganisationConfig.restrictToRoles,
                        { placeholder: intl.formatMessage({ id: 'publist.sidebar.orga_project_role_restriction_placeholder' })},
                        getLanguageFromLocale(intl.locale))}
                      </FormField>

                      {/* Filter works for project's / organization's contributions with certain roles. Exclusion filter */}
                      <FormField
                        label={intl.formatMessage({ id: 'publist.sidebar.orga_project_role_exclude_label' })}
                        help={intl.formatMessage({ id: 'publist.sidebar.orga_project_role_exclude_help' })}
                      >
                        {getRoles((value) => {
                          const temp = config.projectOrganisationConfig;
                          temp.excludeByRoles = value;
                          updateConfigChanged({
                            ...config,
                            projectOrganisationConfig: temp,
                          });
                          configChanged(true);
                        }, config.projectOrganisationConfig.excludeByRoles,
                        { placeholder: intl.formatMessage({ id: 'publist.sidebar.orga_project_role_exclude_placeholder' })},
                        getLanguageFromLocale(intl.locale))}
                      </FormField>
                    </Box>
                  }
                </div>
            </React.Fragment>
          )}
          </div>
          <Divider/>

          {/* Category: Style and language */}
          <div>
            <CategoryToggle open={openCats.citation} onClick={() => setCat('citation')}>
              <FormattedMessage id="publist.sidebar.citation" />
            </CategoryToggle>
            {openCats.citation && (
            <React.Fragment>
              {/* Publication list selection for citation style */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.style_label' }, { a: (...chunks) => <a href="https://www.zotero.org/styles">{chunks}</a> })}
                help={intl.formatMessage({ id: 'publist.sidebar.style_help' })}
              >
                <Vocabulary v={citationStyleList}>
                  {(notations) => {
                    return (
                      <MultiAutocomplete
                        onChange={(e) => {
                          // TODO: MultiAutocomplete component is very ungly right now. Needs rework.
                          const style = e[0].abbr ? e[0].abbr : e[0];
                          updateConfigChanged({ ...config, style });
                          configChanged(true);
                        }}
                        single={true}
                        options={Array.from(notations.keys()).map((style) => {
                          return { abbr: style, name: style };
                        })}
                        tags={config.style ? [config.style] : []}
                        allwaysSuggest={true}
                        inputProps={{ placeholder: intl.formatMessage({ id: 'publist.sidebar.style_placeholder' })}}
                      />
                    );
                  }}
                </Vocabulary>
              </FormField>

              {/* Publication list selection for citation locale */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.locale_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.locale_help' })}
              >
                <Vocabulary v={cslLocaleList}>
                  {(notations: Map<string, string>): ReactElement => {
                    return (
                      <MultiAutocomplete
                        tags={config.locale ? [config.locale] : []}
                        onChange={(e: any) => {
                          updateConfigChanged({
                            ...config,
                            locale: e[0],
                          });
                          configChanged(true);
                        }}
                        single={true}
                        options={Array.from(notations.entries()).map(([abbr, name]) => ({ abbr, name }))}
                        inputProps={{ placeholder: intl.formatMessage({ id: 'publist.sidebar.locale_placeholder' }) }}
                        canDelete={false}
                      />
                    );
                  }}
                </Vocabulary>
              </FormField>
            </React.Fragment>
          )}
          <Divider />

          {/* Category: Grouping and TOC */}
          <CategoryToggle open={openCats.group_content} onClick={() => setCat('group_content')}>
            <FormattedMessage id="publist.sidebar.group_content" />
          </CategoryToggle>
          {openCats.group_content && (
            <React.Fragment>
              {/* Publication list checkbox: Include TOC? */}
              <FormField
                help={intl.formatMessage({ id: 'publist.sidebar.toc_help' })}
                label={intl.formatMessage({ id: 'publist.sidebar.toc_label'})}
                childrenRenderPosition="start"
              >
                <input
                  type="checkbox"
                  id="option-toc"
                  name="toc"
                  checked={toc}
                  onChange={(e) => {
                    updateConfigChanged({ ...config, toc: !toc });
                    configChanged(true);
                  }}
                />
              </FormField>

              {/* Publication list checkbox: Group by publication year? */}
              <FormField
                help={intl.formatMessage({ id: 'publist.sidebar.group_year_help' })}
                label={intl.formatMessage({ id: 'publist.sidebar.group_year_label' })}
                childrenRenderPosition="start"
              >
                <input
                  type="checkbox"
                  id="option-group-by-year"
                  name="group-by-year"
                  checked={groupByYear}
                  onChange={(e) => {
                    updateConfigChanged({
                      ...config,
                      groupByYear: !groupByYear,
                    });
                    configChanged(true);
                  }}
                />
              </FormField>

              {/* Publication list checkbox: Group by publication type? */}
              <FormField
                help={intl.formatMessage({
                  id: config.listType === PubListConfigListTypeEnum.PUBLICATION ? 'publist.sidebar.group_type_help' : 'publist.sidebar.research_data.group_type_help'
                })}
                label={intl.formatMessage({
                  id: config.listType === PubListConfigListTypeEnum.PUBLICATION ? 'publist.sidebar.group_type_label' : 'publist.sidebar.research_data.group_type_label'
                })}
                childrenRenderPosition="start"
              >
                <input
                  type="checkbox"
                  id="option-group-by-type"
                  name="group-by-type"
                  checked={groupByType}
                  onChange={(e) => {
                    updateConfigChanged({
                      ...config,
                      groupByType: !groupByType,
                    });
                    configChanged(true);
                  }}
                />
              </FormField>

              {/* Publication list selection: Choose sorting style */}
              {config.listType === PubListConfigListTypeEnum.PUBLICATION &&
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.sorting_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.sorting_help' })}
              >
                <Select
                  id="option-pubsort"
                  className="input"
                  name="pubsort"
                  onChange={(e: any) => {
                    updateConfigChanged({
                      ...config,
                      pubsort: e.target.value,
                    });
                    configChanged(true);
                  }}
                >
                  <option value="stm">{intl.formatMessage({ id: 'publist.sidebar.sorting.style.stm' })}</option>
                  <option value="anh">{intl.formatMessage({ id: 'publist.sidebar.sorting.style.anh' })}</option>
                </Select>
              </FormField>
              }
            </React.Fragment>
          )}
          <Divider />

          {/* Category: Person related filters */}
          <CategoryToggle open={openCats.person_filter_func} onClick={() => setCat('person_filter_func')}>
            <FormattedMessage id="publist.sidebar.person_filter_func" />
          </CategoryToggle>
          {openCats.person_filter_func && (
            <React.Fragment>
              {/* Publication list selection: Filter publications for person roles (inclusive filter) */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.role_filter_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.role_filter_help' })}
              >
                {getRoles((value) => {
                  updateConfigChanged({
                    ...config,
                    filterByRole: value,
                  });
                  configChanged(true);
                }, config.filterByRole,
                { placeholder: intl.formatMessage({ id: 'publist.sidebar.role_filter_placeholder' })},
                getLanguageFromLocale(intl.locale))}
              </FormField>

              {/* Publication list selection: Filter publications for person roles (exclusive filter) */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.role_exclude_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.role_exclude_help' })}
              >
                {getRoles((value) => {
                  updateConfigChanged({
                    ...config,
                    filterByRoleExcl: value,
                  });
                  configChanged(true);
                }, config.filterByRoleExcl,
                { placeholder: intl.formatMessage({ id: 'publist.sidebar.role_exclude_placeholder' })},
                getLanguageFromLocale(intl.locale))}
              </FormField>

              {/* Publication list selection: Filter for person roles (exclusive filter) */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.role_exclude_person_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.role_exclude_person_help' })}
              >
                {getRoles((value) => {
                  updateConfigChanged({
                    ...config,
                    filterPersonByRole: value,
                  });
                  configChanged(true);
                }, config.filterPersonByRole,
                { placeholder: intl.formatMessage({ id: 'publist.sidebar.role_exclude_person_placeholder' })},
                getLanguageFromLocale(intl.locale))}
              </FormField>

              {/* Publication list selection: Filter for persons (exclusive filter) */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.person_filter_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.person_filter_help' })}
              >
                <PersonSuggest
                  required={false}
                  tags={config.filterPersons}
                  onChange={(refPersons: ReferencedPersonDTO[]): void => {
                    updateConfigChanged({
                      ...config,
                      filterPersons: refPersons,
                    });
                    configChanged(true);
                  }}
                  inputProps={{ placeholder: intl.formatMessage({ id: 'publist.sidebar.person_filter_placeholder' }) }}
                />
              </FormField>
            </React.Fragment>
          )}
          <Divider />

          {/* Category: General filters */}
          <CategoryToggle open={openCats.general_filter} onClick={() => setCat('general_filter')}>
            <FormattedMessage id="publist.sidebar.general_filter" />
          </CategoryToggle>
          {openCats.general_filter && (
            <React.Fragment>
              {config.listType === PubListConfigListTypeEnum.PUBLICATION &&
              <>
              {/* Publication list selection: Filter for publication types (exclusive filter) */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.types_exclude_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.types_exclude_help' })}
              >
                {getPubtypes((e: any) => {
                  updateConfigChanged({ ...config, excludeByType: e });
                  configChanged(true);
                }, config.excludeByType, { placeholder: intl.formatMessage({ id: 'publist.sidebar.types_exclude_placeholder' })},
                getLanguageFromLocale(intl.locale))}
              </FormField>

              {/* Publication list selection: Filter for publication types (inclusive filter) */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.types_filter_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.types_filter_help' })}
              >
                {getPubtypes((e: any) => {
                  updateConfigChanged({ ...config, filterByType: e });
                  configChanged(true);
                }, config.filterByType, { placeholder: intl.formatMessage({ id: 'publist.sidebar.types_filter_placeholder' })},
                getLanguageFromLocale(intl.locale))}
              </FormField>
              </>
              }

              {config.listType === PubListConfigListTypeEnum.RESEARCHDATA &&
              <>
              {/* Resource list selection: Filter for resource types (exclusive filter) */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.research_data.types_exclude_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.research_data.types_exclude_help' })}
              >
                {getResourceTypes((e: any) => {
                  updateConfigChanged({ ...config, excludeByType: e });
                  configChanged(true);
                }, config.excludeByType, { placeholder: intl.formatMessage({ id: 'publist.sidebar.research_data.types_exclude_placeholder' })},
                getLanguageFromLocale(intl.locale))}
              </FormField>

              {/* Resource list selection: Filter for resource types (inclusive filter) */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.research_data.types_filter_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.research_data.types_filter_help' })}
              >
                {getResourceTypes((e: any) => {
                  updateConfigChanged({ ...config, filterByType: e });
                  configChanged(true);
                }, config.filterByType, { placeholder: intl.formatMessage({ id: 'publist.sidebar.research_data.types_filter_placeholder' })},
                getLanguageFromLocale(intl.locale))}
              </FormField>
              </>
              }

              {/* Publication list tags input: Filter for publication years (inclusive filter) */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.years_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.years_help' })}
              >
                <TagsInput
                  required={false}
                  value={yearPeriodsToStrings(config.filterByYear)}
                  onlyUnique={true}
                  onChange={(value) => {
                    updateConfigChanged({
                      ...config,
                      filterByYear: yearStringsToPeriods(value, () =>
                        alertShow(intl.formatMessage({ id: 'publist.sidebar.years_alarm.invalid_year' }))
                      ),
                    });
                    configChanged(true);
                  }}
                  inputProps={{
                    placeholder: intl.formatMessage({ id: 'publist.sidebar.years_placeholder' }),
                  }}
                  renderInput={inputProps => <Input {...inputProps} />}
                />
              </FormField>

              {/* Publication list date range picker: Filter for creation date / period (inclusive filter) */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.created_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.created_help' })}
              >
                <MultipleDateRange
                  value={datePeriodsToStrings(config.filterByCreationDate)}
                  onChange={(ranges: string[]) =>{
                    updateConfigChanged(
                    {...config,
                      filterByCreationDate: dateStringsToPeriods(ranges)
                    });
                    configChanged(true);
                  }}
                  placeholder={intl.formatMessage({ id: 'publist.sidebar.created_placeholder' })}
                />
              </FormField>

              {/* Publication list tags input: Filter for language (inclusive filter) */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.language_filter_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.language_filter_help' })}
              >
                <Vocabulary v={"language_list"} p={"publig"}>
                  {(notations: Map<string, string>): ReactElement => {
                    return (
                      <MultiAutocomplete
                        tags={config.filterByLang}
                        onChange={(e: any) => {
                          updateConfigChanged({
                            ...config,
                            filterByLang: e,
                          });
                          configChanged(true);
                        }}
                        options={Array.from(notations.entries()).map(([abbr, name]) => ({ abbr, name }))}
                        inputProps={{ placeholder: intl.formatMessage({ id: 'publist.sidebar.language_filter_placeholder' }) }}
                      />
                    );
                  }}
                </Vocabulary>
              </FormField>

              {/* Publication list tristate input: Filter for peer review status (green=yes, red=no, grey=all) */}
              <FormField
                help={intl.formatMessage({ id: 'publist.sidebar.peer_filter_help' })}
                label={intl.formatMessage({ id: 'publist.sidebar.peer_filter_label' })}
                childrenRenderPosition="end"
              >
                <TriCheckBox
                  value={filterByPr}
                  onChange={(e) => {
                    console.log(e);
                    updateConfigChanged({
                      ...config,
                      filterByPr: e,
                    });
                    configChanged(true);
                  }}
                />
              </FormField>
            </React.Fragment>
          )}
          <Divider />

          {/* Category: Highlighting publications */}
          <CategoryToggle open={openCats.publication_highlight} onClick={() => setCat('publication_highlight')}>
            <FormattedMessage id="publist.sidebar.publication_highlight" />
          </CategoryToggle>
          {openCats.publication_highlight && (
            <React.Fragment>
              {/* Select publications which appear in a separate "highlighted section" */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.keyIds_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.keyIds_help' })}
              >
                <WorkSuggest
                  tags={config.keyIds}
                  onChange={(keyWorks: ReferencedWorkDTO[]) => {
                    updateConfigChanged({
                      ...config,
                      keyIds: keyWorks
                    });
                    configChanged(true);
                  }}
                />
              </FormField>

              {/* Select publications which should be excluded from the list */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.work_filter_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.work_filter_help' })}
              >
                <WorkSuggest
                  tags={config.filterIds}
                  onChange={(filterWorks: ReferencedWorkDTO[]) => {
                    updateConfigChanged({
                      ...config,
                      filterIds: filterWorks
                    });
                    configChanged(true);
                  }}
                />
              </FormField>

            </React.Fragment>
          )}
          <Divider />

          {/* Category: Change text and add links */}
          <CategoryToggle open={openCats.replace_text_links} onClick={() => setCat('replace_text_links')}>
            <FormattedMessage id="publist.sidebar.replace_text_links" />
          </CategoryToggle>
          {openCats.replace_text_links && (
            <React.Fragment>
              {/* Publication list dual text input: "Search for" and "Replace by" */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.replace_text_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.replace_text_help' })}
              >
                <MultiInputControls<PubListConfigReplacements>
                  add
                  remove
                  lines={replacements}
                  defaultRow={{}}
                  onChange={(r: PubListConfigReplacements[]) => {
                    updateConfigChanged({
                      ...config,
                      replacements: r,
                    });
                    configChanged(true);
                  }}
                  renderLines={(replacement: any, onChange2, actions) => {
                    return (
                      <div className="form-group">
                        <TextInput name={intl.formatMessage({ id: 'publist.sidebar.replace_text_source_label' })}
                                  value={replacement.key}
                                  required={true}
                                  onChange={(key) => onChange2({ ...replacement, key })}
                                  placeholder={intl.formatMessage({ id: 'publist.sidebar.replace_text_source_placeholder' })}
                                  autoFocus={true}
                                  />
                        <TextInput name={intl.formatMessage({ id: 'publist.sidebar.replace_text_target_label' })}
                                  value={replacement.value}
                                  required={false}
                                  onChange={(value) => onChange2({ ...replacement, value })}
                                  placeholder={intl.formatMessage({ id: 'publist.sidebar.replace_text_target_placeholder' })}
                                  />
                        {!!actions.delete && (
                          <div sx={{mt: '1.3em'}}>
                            <DeleteButton onClick={actions.delete} />
                          </div>
                        )}
                      </div>
                    );
                  }}
                  renderEnd={(actionAdd) => (
                    <div sx={{mt: '-0.75em'}}>
                      <AddButton onClick={actionAdd} />
                    </div>
                  )}
                />
              </FormField>

              {/* Publication list tags input: Add linking sources */}
              <FormField
                label={intl.formatMessage({ id: 'publist.sidebar.linking_label' })}
                help={intl.formatMessage({ id: 'publist.sidebar.linking_help' })}
              >
                <Vocabulary v={linkingList}>
                  {(notations: Map<string, string>): ReactNode => (
                    <MultiAutocomplete
                      tags={config.linking}
                      onChange={(value) => {
                        logger.log(value);
                        updateConfigChanged({ ...config, linking: value });
                        configChanged(true);
                      }}
                      options={Array.from(notations).map(([abbr, name]) => ({abbr, name}))}
                      allwaysSuggest={true}
                      inputProps={{ placeholder: intl.formatMessage({ id: 'publist.sidebar.linking_placeholder' }) }}
                    />
                  )}
                </Vocabulary>
              </FormField>
            </React.Fragment>
          )}
          </div>
          {breakpoint > 2 && (
            <Divider />
          )}

          {/* Buttons */}
          <Grid columns={2} py={4} gap={'14px'} sx={{mt: '1em'}}>

            {/* Button Preview */}
            <Button
              sx={{p: '7px 0', border: '3px solid', borderColor: 'primary'}}
              onClick={() => {
                loadPreview(formatConfig(config));
                setShowFilterOptions(!showFilterOptions);
                goBackClick(true);
              }}
              disabled={disabledButtons || loading}>
              <FormattedMessage id="publist.sidebar.reload_btn" />
            </Button>

            {/* Button Save */}
            {storeConfig !== null && (
            <>
              <Button
                sx={{p: '7px 0', border: '3px solid', borderColor: 'primary'}}
                onClick={() => storeConfig(formatConfig(config)).then((code: number) => {
                  if (code >= 200 && code < 300) setChanged(false);
                })}
                disabled={disabledButtons || loading}
              >
                <FormattedMessage id="publist.sidebar.save_btn" />
              </Button>
              {/* Button Clear */}
              <Button
                sx={{color: 'primary', border:'3px solid', backgroundColor: 'white', p: '7px 0'}}
                onClick={() => {
                  clearAllInput(config.listType);
                  setChanged(false);
                }}
                disabled={loading}
              >
                <FormattedMessage id="publist.sidebar.clear_btn" />
              </Button>
            </>
            )}

            {/* Button Save New */}
            <OAuth2Gate isNotAnonymous>
              <Button
                sx={{
                  backgroundColor: 'primary',
                  p: '7px 0',
                  border: '3px solid',
                  borderColor: 'primary'
                }}
                onClick={() => storeNewConfig(formatConfig(config)).then((code: number) => {
                  if (code >= 200 && code < 300) setChanged(false);
                })}
                disabled={disabledButtons || loading}>
                  {storeConfig === null && (
                    <FormattedMessage id="publist.sidebar.save_btn" />
                  )}
                  {storeConfig !== null && (
                    <FormattedMessage id="publist.sidebar.save_new_btn" />
                  )}
              </Button>
            </OAuth2Gate>

            {/* Button Save New */}
            {breakpoint <= 2 &&
              <OAuth2Gate isAnonymous>
                <Button
                  sx={{
                    backgroundColor: 'primary',
                    p: '7px 0',
                    border: '3px solid',
                    borderColor: 'primary'
                  }}
                  onClick={() => {
                    setShowFilterOptions(!showFilterOptions);
                    goBackClick(true);
                    setSaveAnonymous(true);
                  }}
                  disabled={disabledButtons || loading}>
                    {storeConfig === null && (
                      <FormattedMessage id="publist.sidebar.save_btn" />
                    )}
                    {storeConfig !== null && (
                      <FormattedMessage id="publist.sidebar.save_new_btn" />
                    )}
                </Button>
              </OAuth2Gate>
            }

            {/* Button Clear */}
            {storeConfig === null && (
            <Button
              sx={{color: 'primary', border:'3px solid', backgroundColor: 'white', p: '7px 0'}}
              onClick={() => {
                clearAllInput(config.listType);
                setChanged(false);
              }}
              disabled={loading}
            >
              <FormattedMessage id="publist.sidebar.clear_btn" />
            </Button>
            )}
          </Grid>
        </div>
      )}

      {/* Content/Preview section */}
      {mobileView &&
        <div className="content" sx={{ mt: !showFilterOptions && !loading && mobileView ? '-22px' : 0}}>
          {!showFilterOptions && saveAnonymous && mobileView &&
            <OAuth2Gate isAnonymous>
              <div sx={{ mb: '24px', display: 'inline-flex', '&:hover': { cursor: 'pointer'} }}>
                <i className="icon-links_aktiv" sx={{ fontSize: '9px', mr: '5px', alignSelf: 'center' }} />
                <div sx={{color: "primary", fontWeight: '600', mb: '2px', textDecoration: 'underline'}} onClick={()=>{setShowFilterOptions(!showFilterOptions);goBackClick(false);setSaveAnonymous(false);}}>
                  <FormattedMessage id="work.upload.button.back" />
                </div>
              </div>
              <Text sx={{
                color: "primary",
                fontWeight: "bold",
                mt: '-8px'
              }}>
                <FormattedMessage id="publist.message.logininfo" /> <br />
                <OAuth2Sender
                  render={({ authUrl }) => (
                    <Button mt={5} py={'8px'} px={'16px'} onClick={()=>{
                      window.location.href = authUrl;
                    }}><FormattedMessage id="header.login" /></Button>
                  )}
                />
              </Text>
            </OAuth2Gate>
          }

          {!showFilterOptions && !loading && !saveAnonymous ? (
            <React.Fragment>
              {preview && breakpoint <= 2 && (
                <>
                  <div sx={{ mb: '24px', display: 'inline-flex', '&:hover': { cursor: 'pointer'} }}>
                    <i className="icon-links_aktiv" sx={{ fontSize: '9px', mr: '5px', alignSelf: 'center' }} onClick={()=>{setShowFilterOptions(!showFilterOptions);goBackClick(false);}} />
                    <div sx={{color: "primary", fontWeight: '600', mb: '2px', textDecoration: 'underline'}} onClick={()=>{setShowFilterOptions(!showFilterOptions);goBackClick(false);}}>
                      <FormattedMessage id="work.upload.button.back" />
                    </div>
                  </div>
                  {!emptyList &&
                  <Styled.h3 className='result-header'>
                    <FormattedMessage id="publist.message.results" />
                  </Styled.h3>
                  }

                </>
              )}
              {preview && (themeName !== 'rub') && <Preview content={preview} ></Preview>}
              {preview && (themeName === 'rub') && <PreviewRUB content={preview} ></PreviewRUB>}
            </React.Fragment>
          ) : (
            <></>
          )}
        </div>
      }
    </>
  );
};
